import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileUp,
  RefreshCw,
  ShieldCheck,
  Trash2,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import { useAuth } from '../context/AuthContext';

type TargetProduct = 'Microsoft Sentinel' | 'Microsoft Defender for Endpoint';
type RowStatus = 'ready' | 'warning' | 'error' | 'duplicate';
type FilterStatus = 'all' | RowStatus;

interface BulkValidationItem {
  rowNumber: number;
  indicatorValue: string;
  normalizedValue: string;
  indicatorType: string;
  observableType: string;
  name: string;
  status: RowStatus;
  errors: string[];
  warnings: string[];
  duplicateOfRow?: number | null;
}

interface BulkSummary {
  totalRows: number;
  ready: number;
  warnings: number;
  errors: number;
  duplicates: number;
}

interface BulkValidationResponse {
  ok: boolean;
  customerId: string;
  targetProduct: TargetProduct;
  filename: string;
  customer?: {
    id: string;
    name: string;
    tenantId?: string;
    subscription?: string;
    resourceGroup?: string;
    workspaceId?: string;
    workspaceName?: string;
    lighthouseMode?: boolean;
    authMode?: string;
  };
  summary: BulkSummary;
  items: BulkValidationItem[];
  message?: string;
}

interface BulkUploadItem extends BulkValidationItem {
  uploadStatus: 'succeeded' | 'failed' | 'skipped';
  remoteId?: string | null;
  uploadError?: string | null;
}

interface BulkUploadResponse {
  ok: boolean;
  customerId: string;
  targetProduct: TargetProduct;
  filename: string;
  summary: {
    requested: number;
    succeeded: number;
    failed: number;
    skipped: number;
  };
  items: BulkUploadItem[];
  message?: string;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;

function csvEscape(value: unknown): string {
  const text = String(value ?? '');
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadText(filename: string, content: string, type = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function sentinelTemplate(): string {
  const headers = ['IndicatorValue', 'IndicatorType', 'Name', 'Description', 'Source', 'ValidUntilUtc', 'Tags', 'Confidence', 'TLP'];
  const row = [
    '185.220.101.45',
    'ipv4',
    'Known malicious infrastructure',
    'Bulk imported from KloudSecure',
    'KloudSecure',
    new Date(Date.now() + 30 * 86400000).toISOString(),
    'bulk-import|customer-submission',
    '75',
    'white',
  ];
  return `${headers.map(csvEscape).join(',')}\r\n${row.map(csvEscape).join(',')}\r\n`;
}

function mdeTemplate(): string {
  const headers = ['IndicatorValue', 'IndicatorType', 'Name', 'Description', 'ValidUntilUtc', 'Tags', 'Action', 'Severity', 'GenerateAlert', 'RecommendedActions'];
  const row = [
    'malware-c2.example',
    'domain',
    'Known malicious domain',
    'Bulk imported from KloudSecure',
    new Date(Date.now() + 30 * 86400000).toISOString(),
    'bulk-import|customer-submission',
    'Block',
    'Medium',
    'true',
    'Review related alerts and scope before closing.',
  ];
  return `${headers.map(csvEscape).join(',')}\r\n${row.map(csvEscape).join(',')}\r\n`;
}

function statusClass(status: RowStatus): string {
  if (status === 'ready') return 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200';
  if (status === 'warning') return 'border-amber-400/30 bg-amber-500/10 text-amber-200';
  if (status === 'duplicate') return 'border-violet-400/30 bg-violet-500/10 text-violet-200';
  return 'border-rose-400/30 bg-rose-500/10 text-rose-200';
}

export function BulkIocEntryTab() {
  const { customers, activeCustomerId, activeCustomer } = useCustomers();
  const { canCapability } = useAuth();
  const canValidate = canCapability('threat_intel', 'validate_ioc');
  const canSentinelUpload = canCapability('threat_intel', 'create_sentinel_ioc');
  const canMdeUpload = canCapability('threat_intel', 'create_mde_ioc');

  // Deliberately starts blank. Bulk indicator writes must never inherit the
  // global/default customer silently; an analyst must explicitly select the
  // destination customer before validation or upload is enabled.
  const [customerId, setCustomerId] = useState('');
  const [target, setTarget] = useState<TargetProduct>('Microsoft Sentinel');
  const selectedCustomer = customers.find((customer) => customer.id === customerId);

  const [file, setFile] = useState<File | null>(null);
  const [content, setContent] = useState('');
  const [fileError, setFileError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const [source, setSource] = useState('KloudSecure');
  const [expirationDays, setExpirationDays] = useState(30);
  const [confidence, setConfidence] = useState(75);
  const [tlp, setTlp] = useState('white');
  const [mdeAction, setMdeAction] = useState('Block');
  const [mdeSeverity, setMdeSeverity] = useState('Medium');
  const [mdeGenerateAlert, setMdeGenerateAlert] = useState(true);
  const [mdeRecommendedActions, setMdeRecommendedActions] = useState('Review related alerts and scope before closing.');

  const [validating, setValidating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [validation, setValidation] = useState<BulkValidationResponse | null>(null);
  const [uploadResult, setUploadResult] = useState<BulkUploadResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const uploadAllowed = target === 'Microsoft Sentinel' ? canSentinelUpload : canMdeUpload;
  const customerReady = Boolean(customerId && selectedCustomer);
  const fileReady = Boolean(file && content);
  const canSubmit = Boolean(validation && validation.summary.ready + validation.summary.warnings > 0 && uploadAllowed && customerReady && fileReady);

  const filteredItems = useMemo(() => {
    const rows = validation?.items ?? [];
    return filter === 'all' ? rows : rows.filter((row) => row.status === filter);
  }, [filter, validation]);

  useEffect(() => {
    // Changing the target/customer invalidates prior validation and upload
    // results. This prevents a Sentinel validation from being submitted to MDE
    // (or a Customer A validation from being submitted to Customer B).
    setValidation(null);
    setUploadResult(null);
    setConfirmOpen(false);
    setMessage(null);
  }, [customerId, target]);

  function defaults() {
    return {
      source,
      expirationDays,
      confidence,
      tlp,
      mdeAction,
      mdeSeverity,
      mdeGenerateAlert,
      mdeRecommendedActions,
    };
  }

  async function selectFile(next: File) {
    setFileError(null);
    setMessage(null);
    setValidation(null);
    setUploadResult(null);
    setConfirmOpen(false);
    if (next.size > MAX_FILE_BYTES) {
      setFile(null);
      setContent('');
      setFileError('The file is larger than the 5 MB bulk-import limit.');
      return;
    }
    if (!/\.(csv|txt|json)$/i.test(next.name)) {
      setFile(null);
      setContent('');
      setFileError('Use a CSV, TXT, or JSON file.');
      return;
    }
    try {
      const text = await next.text();
      if (!text.trim()) throw new Error('The selected file is empty.');
      setFile(next);
      setContent(text);
    } catch (e) {
      setFile(null);
      setContent('');
      setFileError(e instanceof Error ? e.message : 'Could not read the selected file.');
    }
  }

  async function validateAll() {
    if (!canValidate || !customerReady || !fileReady || !file) return;
    setValidating(true);
    setMessage(null);
    setUploadResult(null);
    setConfirmOpen(false);
    try {
      const result = await apiFetch<BulkValidationResponse>('/threat-intel/ioc/bulk/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, targetProduct: target, filename: file.name, content, defaults: defaults() }),
      });
      setValidation(result);
      setFilter('all');
      setMessage(result.message ?? `Validated ${result.summary.totalRows} row(s).`);
    } catch (e) {
      setValidation(null);
      setMessage(e instanceof Error ? e.message : 'Bulk validation failed.');
    } finally {
      setValidating(false);
    }
  }

  async function uploadValidated() {
    if (!canSubmit || !file) return;
    setUploading(true);
    setMessage(null);
    try {
      const path = target === 'Microsoft Sentinel'
        ? '/threat-intel/ioc/bulk/sentinel/upload'
        : '/threat-intel/ioc/bulk/mde/upload';
      const result = await apiFetch<BulkUploadResponse>(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, targetProduct: target, filename: file.name, content, defaults: defaults(), duplicatePolicy: 'skip' }),
      });
      setUploadResult(result);
      setConfirmOpen(false);
      setMessage(result.message ?? `Bulk upload finished: ${result.summary.succeeded} succeeded, ${result.summary.failed} failed.`);
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Bulk upload failed.');
    } finally {
      setUploading(false);
    }
  }

  function clearFile() {
    setFile(null);
    setContent('');
    setFileError(null);
    setValidation(null);
    setUploadResult(null);
    setConfirmOpen(false);
    setMessage(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  function downloadTemplate() {
    if (target === 'Microsoft Sentinel') {
      downloadText('KloudSecure-Bulk-IoC-Sentinel-Template.csv', sentinelTemplate());
    } else {
      downloadText('KloudSecure-Bulk-IoC-MDE-Template.csv', mdeTemplate());
    }
  }

  function downloadFailedRows() {
    const failed = uploadResult?.items.filter((item) => item.uploadStatus === 'failed') ?? [];
    if (!failed.length) return;
    const headers = ['Row', 'IndicatorValue', 'IndicatorType', 'Name', 'Error'];
    const rows = failed.map((item) => [item.rowNumber, item.indicatorValue, item.indicatorType, item.name, item.uploadError ?? item.errors.join('; ')]);
    downloadText('KloudSecure-Bulk-IoC-Failed-Rows.csv', `${headers.join(',')}\r\n${rows.map((row) => row.map(csvEscape).join(',')).join('\r\n')}\r\n`);
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-400">Bulk IoC workflow</div>
            <h3 className="mt-1 text-xl font-black text-foreground">Validate first, then write to the selected customer</h3>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">Download a destination-specific template, upload CSV/TXT/JSON, review row-level validation, and only then submit the ready indicators. Customer selection is explicit by design.</p>
          </div>
          <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-sm font-bold text-cyan-100 hover:bg-cyan-500/20">
            <Download size={16} /> Download {target === 'Microsoft Sentinel' ? 'Sentinel' : 'MDE'} template
          </button>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h4 className="font-black text-foreground">1. Customer and destination</h4>
          <p className="mt-1 text-xs text-muted-foreground">No default tenant is used. Choose the customer that will receive these indicators.</p>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Customer <span className="text-rose-400">*</span></label>
              <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
                <option value="">Select customer explicitly…</option>
                {customers.filter((customer) => customer.enabled !== false).map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
              </select>
              <p className="mt-1 text-[11px] text-muted-foreground">Current global dashboard customer: {activeCustomer?.name ?? activeCustomerId ?? 'none'}. Bulk upload does not inherit it automatically.{selectedCustomer && activeCustomerId !== selectedCustomer.id ? ' The explicit bulk target differs from the global dashboard customer.' : ''}</p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Destination <span className="text-rose-400">*</span></label>
              <select value={target} onChange={(e) => setTarget(e.target.value as TargetProduct)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground">
                <option>Microsoft Sentinel</option>
                <option>Microsoft Defender for Endpoint</option>
              </select>
              <p className="mt-1 text-[11px] text-muted-foreground">One destination per file keeps validation and failure handling deterministic.</p>
            </div>
          </div>

          <div className={`mt-4 rounded-xl border p-4 ${selectedCustomer ? 'border-cyan-400/25 bg-cyan-500/10' : 'border-amber-400/25 bg-amber-500/10'}`}>
            {selectedCustomer ? (
              <div className="grid gap-3 text-xs sm:grid-cols-2 xl:grid-cols-3">
                <div><div className="text-muted-foreground">Target customer</div><div className="mt-1 font-black text-foreground">{selectedCustomer.name}</div></div>
                <div><div className="text-muted-foreground">Tenant</div><div className="mt-1 break-all font-mono text-foreground">{selectedCustomer.tenantId || 'Not configured'}</div></div>
                <div><div className="text-muted-foreground">Workspace</div><div className="mt-1 font-black text-foreground">{selectedCustomer.workspaceName || 'Not configured'}</div></div>
                <div><div className="text-muted-foreground">Subscription</div><div className="mt-1 break-all font-mono text-foreground">{selectedCustomer.subscription || '-'}</div></div>
                <div><div className="text-muted-foreground">Resource group</div><div className="mt-1 font-mono text-foreground">{selectedCustomer.resourceGroup || '-'}</div></div>
                <div><div className="text-muted-foreground">Access mode</div><div className="mt-1 font-black text-foreground">{selectedCustomer.lighthouseMode ? 'Azure Lighthouse' : 'Customer SPN'}</div></div>
                <div><div className="text-muted-foreground">Destination</div><div className="mt-1 font-black text-cyan-200">{target}</div></div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm font-semibold text-amber-200"><AlertTriangle size={16} /> Select a customer before validation or upload.</div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <h4 className="font-black text-foreground">2. Destination defaults</h4>
          <p className="mt-1 text-xs text-muted-foreground">Template values override these defaults when supplied on a row.</p>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-bold text-muted-foreground">Expiration days</label>
              <input type="number" min={1} max={365} value={expirationDays} onChange={(e) => setExpirationDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            </div>
            {target === 'Microsoft Sentinel' ? (
              <>
                <div><label className="text-xs font-bold text-muted-foreground">Source</label><input value={source} onChange={(e) => setSource(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></div>
                <div><label className="text-xs font-bold text-muted-foreground">Confidence</label><input type="number" min={0} max={100} value={confidence} onChange={(e) => setConfidence(Math.max(0, Math.min(100, Number(e.target.value) || 0)))} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></div>
                <div><label className="text-xs font-bold text-muted-foreground">TLP</label><select value={tlp} onChange={(e) => setTlp(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"><option value="white">White</option><option value="green">Green</option><option value="amber">Amber</option><option value="red">Red</option></select></div>
              </>
            ) : (
              <>
                <div><label className="text-xs font-bold text-muted-foreground">Action</label><select value={mdeAction} onChange={(e) => setMdeAction(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"><option>Block</option><option>BlockAndRemediate</option><option>Audit</option><option>Warn</option><option>Allowed</option><option>Alert</option><option>AlertAndBlock</option></select></div>
                <div><label className="text-xs font-bold text-muted-foreground">Severity</label><select value={mdeSeverity} onChange={(e) => setMdeSeverity(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"><option>Informational</option><option>Low</option><option>Medium</option><option>High</option></select></div>
                <label className="mt-6 inline-flex items-center gap-2 text-sm text-foreground"><input type="checkbox" checked={mdeGenerateAlert} onChange={(e) => setMdeGenerateAlert(e.target.checked)} /> Generate alert</label>
                <div className="md:col-span-2"><label className="text-xs font-bold text-muted-foreground">Recommended actions</label><input value={mdeRecommendedActions} onChange={(e) => setMdeRecommendedActions(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></div>
              </>
            )}
          </div>
        </section>
      </div>

      <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 className="font-black text-foreground">3. Upload and parse</h4>
            <p className="mt-1 text-xs text-muted-foreground">CSV templates are recommended. TXT supports one indicator per line; JSON accepts strings or objects with indicator/value fields.</p>
          </div>
          {file && <button type="button" onClick={clearFile} className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-bold text-muted-foreground hover:bg-muted"><Trash2 size={14} /> Clear file</button>}
        </div>

        <div
          className={`mt-4 cursor-pointer rounded-2xl border-2 border-dashed p-9 text-center transition ${dragging ? 'border-cyan-400 bg-cyan-500/10' : 'border-border bg-background/40 hover:border-cyan-400/50 hover:bg-cyan-500/5'}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); const next = e.dataTransfer.files?.[0]; if (next) void selectFile(next); }}
        >
          <FileUp size={30} className="mx-auto text-cyan-300" />
          <div className="mt-3 font-black text-foreground">{file ? file.name : 'Drop a bulk IoC file here or click to browse'}</div>
          <div className="mt-1 text-xs text-muted-foreground">CSV, TXT, JSON · Maximum 5 MB</div>
          {file && <div className="mt-2 text-[11px] text-cyan-200">{(file.size / 1024).toFixed(1)} KB loaded and waiting for validation.</div>}
        </div>
        <input ref={fileRef} className="hidden" type="file" accept=".csv,.txt,.json" onChange={(e) => { const next = e.target.files?.[0]; if (next) void selectFile(next); }} />
        {fileError && <div className="mt-3 rounded-xl border border-rose-400/30 bg-rose-500/10 p-3 text-sm text-rose-200">{fileError}</div>}

        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={() => void validateAll()} disabled={!canValidate || !customerReady || !fileReady || validating} className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-black text-slate-950 disabled:cursor-not-allowed disabled:opacity-40">
            {validating ? <RefreshCw size={16} className="animate-spin" /> : <ShieldCheck size={16} />} {validating ? 'Validating…' : 'Validate all'}
          </button>
          <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-bold text-foreground hover:bg-muted"><FileSpreadsheet size={16} /> Template</button>
        </div>
      </section>

      {validation && (
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h4 className="font-black text-foreground">4. Validation preview</h4>
              <p className="mt-1 text-xs text-muted-foreground">Duplicates are detected within this import and skipped. Existing destination behavior is left to the Microsoft API; any rejected/conflicting rows are returned as failures and can be exported for review.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <SummaryPill label="Total" value={validation.summary.totalRows} tone="slate" />
              <SummaryPill label="Ready" value={validation.summary.ready} tone="green" />
              <SummaryPill label="Warnings" value={validation.summary.warnings} tone="amber" />
              <SummaryPill label="Errors" value={validation.summary.errors} tone="rose" />
              <SummaryPill label="Duplicates" value={validation.summary.duplicates} tone="violet" />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {(['all', 'ready', 'warning', 'error', 'duplicate'] as FilterStatus[]).map((status) => (
              <button key={status} type="button" onClick={() => setFilter(status)} className={`rounded-lg border px-3 py-1.5 text-xs font-bold capitalize ${filter === status ? 'border-cyan-400 bg-cyan-500/15 text-cyan-100' : 'border-border text-muted-foreground hover:bg-muted'}`}>{status}</button>
            ))}
          </div>

          <div className="mt-4 overflow-x-auto rounded-xl border border-border">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground"><tr><th className="px-3 py-2">Row</th><th className="px-3 py-2">Type</th><th className="px-3 py-2">Indicator</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Validation</th></tr></thead>
              <tbody>
                {filteredItems.slice(0, 250).map((item) => (
                  <tr key={`${item.rowNumber}-${item.normalizedValue}`} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-mono text-muted-foreground">{item.rowNumber}</td>
                    <td className="px-3 py-2 font-semibold text-foreground">{item.indicatorType}</td>
                    <td className="max-w-[320px] break-all px-3 py-2 font-mono text-foreground">{item.normalizedValue || item.indicatorValue}</td>
                    <td className="max-w-[260px] px-3 py-2 text-muted-foreground">{item.name}</td>
                    <td className="px-3 py-2"><span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase ${statusClass(item.status)}`}>{item.status}</span></td>
                    <td className="max-w-[420px] px-3 py-2">
                      {item.errors.length > 0 && <div className="text-rose-300">{item.errors.join('; ')}</div>}
                      {item.warnings.length > 0 && <div className="text-amber-200">{item.warnings.join('; ')}</div>}
                      {item.errors.length === 0 && item.warnings.length === 0 && <div className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 size={13} /> Valid</div>}
                    </td>
                  </tr>
                ))}
                {!filteredItems.length && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No rows match this filter.</td></tr>}
              </tbody>
            </table>
          </div>
          {filteredItems.length > 250 && <div className="mt-2 text-[11px] text-muted-foreground">Showing the first 250 rows for this filter.</div>}

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-background/50 p-4">
            <div>
              <div className="font-black text-foreground">{validation.summary.ready + validation.summary.warnings} indicator(s) eligible for submission</div>
              <div className="mt-1 text-xs text-muted-foreground">Errors and duplicate rows are skipped automatically.</div>
            </div>
            <button type="button" onClick={() => setConfirmOpen(true)} disabled={!canSubmit || uploading} className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-black text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"><UploadCloud size={16} /> Review & submit</button>
          </div>
        </section>
      )}

      {confirmOpen && validation && selectedCustomer && (
        <section className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-5 shadow-sm">
          <div className="flex items-start gap-3"><AlertTriangle size={22} className="mt-0.5 text-amber-300" /><div className="flex-1"><h4 className="font-black text-amber-100">Confirm bulk indicator write</h4><p className="mt-1 text-sm text-amber-50/80">You are about to create {validation.summary.ready + validation.summary.warnings} indicator(s) in <b>{selectedCustomer.name}</b> using <b>{target}</b>. This page intentionally requires this second confirmation to reduce cross-tenant mistakes.</p><div className="mt-3 grid gap-2 text-xs text-amber-50/80 sm:grid-cols-2 lg:grid-cols-4"><div>Tenant<br /><code>{selectedCustomer.tenantId || '-'}</code></div><div>Workspace<br /><b>{selectedCustomer.workspaceName || '-'}</b></div><div>Ready<br /><b>{validation.summary.ready}</b></div><div>Warnings<br /><b>{validation.summary.warnings}</b></div></div><div className="mt-4 flex flex-wrap gap-2"><button type="button" onClick={() => void uploadValidated()} disabled={uploading} className="inline-flex items-center gap-2 rounded-xl bg-amber-300 px-4 py-2 text-sm font-black text-slate-950 disabled:opacity-50">{uploading ? <RefreshCw size={16} className="animate-spin" /> : <ShieldCheck size={16} />} {uploading ? 'Submitting…' : `Confirm upload to ${target === 'Microsoft Sentinel' ? 'Sentinel' : 'MDE'}`}</button><button type="button" onClick={() => setConfirmOpen(false)} disabled={uploading} className="rounded-xl border border-amber-200/30 px-4 py-2 text-sm font-bold text-amber-100">Cancel</button></div></div></div>
        </section>
      )}

      {uploadResult && (
        <section className="rounded-2xl border border-border bg-card p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div><h4 className="font-black text-foreground">Upload result</h4><p className="mt-1 text-xs text-muted-foreground">{uploadResult.message || 'Bulk submission completed.'}</p></div>
            <div className="flex flex-wrap gap-2"><SummaryPill label="Requested" value={uploadResult.summary.requested} tone="slate" /><SummaryPill label="Succeeded" value={uploadResult.summary.succeeded} tone="green" /><SummaryPill label="Skipped" value={uploadResult.summary.skipped} tone="violet" /><SummaryPill label="Failed" value={uploadResult.summary.failed} tone="rose" /></div>
          </div>
          {uploadResult.summary.failed > 0 && <button type="button" onClick={downloadFailedRows} className="mt-4 inline-flex items-center gap-2 rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-sm font-bold text-rose-100"><Download size={16} /> Download failed rows</button>}
          <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {uploadResult.items.filter((item) => item.uploadStatus !== 'succeeded').slice(0, 60).map((item) => <div key={`${item.rowNumber}-${item.normalizedValue}`} className={`rounded-xl border p-3 text-xs ${item.uploadStatus === 'failed' ? 'border-rose-400/25 bg-rose-500/10' : 'border-violet-400/25 bg-violet-500/10'}`}><div className="flex items-center gap-2 font-black text-foreground">{item.uploadStatus === 'failed' ? <XCircle size={14} className="text-rose-300" /> : <AlertTriangle size={14} className="text-violet-300" />} Row {item.rowNumber} · {item.indicatorType}</div><div className="mt-1 break-all font-mono text-muted-foreground">{item.normalizedValue}</div><div className="mt-1 text-rose-200">{item.uploadError || item.warnings.join('; ') || 'Skipped by safe duplicate policy.'}</div></div>)}
          </div>
        </section>
      )}

      {message && <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/10 p-3 text-sm text-cyan-100">{message}</div>}
    </div>
  );
}

function SummaryPill({ label, value, tone }: { label: string; value: number; tone: 'slate' | 'green' | 'amber' | 'rose' | 'violet' }) {
  const classes = tone === 'green' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200'
    : tone === 'amber' ? 'border-amber-400/30 bg-amber-500/10 text-amber-200'
      : tone === 'rose' ? 'border-rose-400/30 bg-rose-500/10 text-rose-200'
        : tone === 'violet' ? 'border-violet-400/30 bg-violet-500/10 text-violet-200'
          : 'border-slate-400/20 bg-slate-500/10 text-slate-200';
  return <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-black ${classes}`}>{label} <b>{value}</b></span>;
}
